import React, { useState, useEffect } from 'react';
import { conversionService } from '../services/conversionService.js';

const Conversoes = () => {
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedConversion, setSelectedConversion] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [salesPerformance, setSalesPerformance] = useState(null);

  // Estados para formulário de criação
  const [formData, setFormData] = useState({
    leadId: '',
    type: 'ECF_BALANCED',
    value: '',
    expectedIrr: '',
    creditsCount: '',
    averageMaturity: '',
    channel: 'WEBSITE',
    salesRep: '',
    acquisitionCost: '',
    structuringFee: '',
    managementFee: ''
  });

  // Carregar conversões
  const loadConversions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await conversionService.getConversions(filters, page, 20);

      setConversions(response.conversions);
      setTotalPages(Math.ceil(response.total / 20));
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao carregar conversões:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar métricas
  const loadMetrics = async () => {
    try {
      const [metricsData, performanceData] = await Promise.all([
        conversionService.getConversionMetrics(),
        conversionService.getSalesPerformance()
      ]);
      setMetrics(metricsData);
      setSalesPerformance(performanceData);
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
    }
  };

  useEffect(() => {
    loadConversions();
    loadMetrics();
  }, [filters]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const handleCreateConversion = async (e) => {
    e.preventDefault();
    try {
      const newConversion = await conversionService.createConversion(formData);
      setConversions(prev => [newConversion, ...prev]);
      setShowCreateModal(false);
      resetForm();
      loadMetrics();
    } catch (error) {
      console.error('Erro ao criar conversão:', error);
      alert('Erro ao criar conversão. Verifique os dados.');
    }
  };

  const handleConversionClick = (conversion) => {
    setSelectedConversion(conversion);
    setShowDetailModal(true);
  };

  const handleStatusChange = async (conversionId, newStatus) => {
    try {
      await conversionService.updateConversionStatus(conversionId, newStatus);
      loadConversions(currentPage);
      loadMetrics();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status da conversão.');
    }
  };

  const resetForm = () => {
    setFormData({
      leadId: '',
      type: 'ECF_BALANCED',
      value: '',
      expectedIrr: '',
      creditsCount: '',
      averageMaturity: '',
      channel: 'WEBSITE',
      salesRep: '',
      acquisitionCost: '',
      structuringFee: '',
      managementFee: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  if (loading && conversions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando conversões...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>

        {/* Header com métricas */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Conversões e Receita</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Nova Conversão
            </button>
          </div>

          {/* Cards de métricas */}
          {metrics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Total de Conversões</h3>
                <p className="text-3xl font-bold text-gray-900">{metrics.total}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Valor Total</h3>
                <p className="text-3xl font-bold text-green-600">{conversionService.formatCurrency(metrics.totalValue)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Ticket Médio</h3>
                <p className="text-3xl font-bold text-blue-600">{conversionService.formatCurrency(metrics.averageValue)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">IRR Médio</h3>
                <p className="text-3xl font-bold text-purple-600">{conversionService.formatPercentage(metrics.averageIrr)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <select
                value={filters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os tipos</option>
                <option value="ECF_PREMIUM">ECF Premium</option>
                <option value="ECF_BALANCED">ECF Balanced</option>
                <option value="ECF_GROWTH">ECF Growth</option>
                <option value="CUSTOM">Customizado</option>
              </select>
            </div>
            <div>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="PENDING">Pendente</option>
                <option value="STRUCTURED">Estruturada</option>
                <option value="FUNDED">Financiada</option>
                <option value="ACTIVE">Ativa</option>
                <option value="COMPLETED">Concluída</option>
                <option value="CANCELLED">Cancelada</option>
              </select>
            </div>
            <div>
              <input
                type="number"
                placeholder="Valor mínimo"
                value={filters.valueMin || ''}
                onChange={(e) => handleFilterChange('valueMin', e.target.value || undefined)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="number"
                placeholder="Valor máximo"
                value={filters.valueMax || ''}
                onChange={(e) => handleFilterChange('valueMax', e.target.value || undefined)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Lista de conversões */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead / Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo ECF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IRR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Canal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {conversions.map((conversion) => (
                  <tr
                    key={conversion.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleConversionClick(conversion)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {conversion.lead?.name || `Lead #${conversion.leadId}`}
                        </div>
                        <div className="text-sm text-gray-500">
                          {conversion.lead?.email}
                        </div>
                        {conversion.lead?.company && (
                          <div className="text-sm text-gray-500">
                            {conversion.lead?.company}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {conversionService.getConversionTypeLabel(conversion.type)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {conversion.creditsCount ? `${conversion.creditsCount} créditos` : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {conversionService.formatCurrency(conversion.value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${conversionService.getIrrPerformanceColor(conversion.expectedIrr)}`}>
                        {conversionService.formatPercentage(conversion.expectedIrr)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={conversion.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(conversion.id, e.target.value);
                        }}
                        className={`text-xs px-2 py-1 rounded-full border-0 ${conversionService.getConversionStatusColor(conversion.status)} !text-gray-900`}
                      >
                        <option value="PENDING">Pendente</option>
                        <option value="STRUCTURED">Estruturada</option>
                        <option value="FUNDED">Financiada</option>
                        <option value="ACTIVE">Ativa</option>
                        <option value="COMPLETED">Concluída</option>
                        <option value="CANCELLED">Cancelada</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {conversion.channel}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(conversion.convertedAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConversionClick(conversion);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Ver detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => loadConversions(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => loadConversions(Math.min(currentPage + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(currentPage * 20, conversions.length)}</span> de{' '}
                    <span className="font-medium">{metrics?.total || 0}</span> conversões
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => loadConversions(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => loadConversions(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => loadConversions(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ›
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Performance por Canal */}
        {salesPerformance && salesPerformance.channelPerformance?.length > 0 && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance por Canal</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversões</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Médio</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxa de Conversão</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {salesPerformance.channelPerformance.map((channel, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {channel.channel}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {channel.conversions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {conversionService.formatCurrency(channel.totalValue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {conversionService.formatCurrency(channel.averageValue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            channel.conversionRate >= 20 ? 'bg-green-100 text-green-700' :
                            channel.conversionRate >= 10 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {channel.conversionRate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de criação de conversão */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <form onSubmit={handleCreateConversion}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Nova Conversão</h3>
              </div>

              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID do Lead *
                    </label>
                    <input
                      type="text"
                      name="leadId"
                      required
                      value={formData.leadId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="ID do lead convertido"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo ECF *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="ECF_PREMIUM">ECF Premium</option>
                      <option value="ECF_BALANCED">ECF Balanced</option>
                      <option value="ECF_GROWTH">ECF Growth</option>
                      <option value="CUSTOM">Customizado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor da Carteira *
                    </label>
                    <input
                      type="number"
                      name="value"
                      required
                      value={formData.value}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IRR Esperado (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="expectedIrr"
                      value={formData.expectedIrr}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="18.5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade de Créditos
                    </label>
                    <input
                      type="number"
                      name="creditsCount"
                      value={formData.creditsCount}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prazo Médio (meses)
                    </label>
                    <input
                      type="number"
                      name="averageMaturity"
                      value={formData.averageMaturity}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="36"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Canal *
                    </label>
                    <select
                      name="channel"
                      value={formData.channel}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="WEBSITE">Site</option>
                      <option value="PARTNERSHIP">Parceria</option>
                      <option value="REFERRAL">Indicação</option>
                      <option value="LINKEDIN">LinkedIn</option>
                      <option value="EVENT">Evento</option>
                      <option value="COLD_OUTREACH">Prospecção</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Representante de Vendas
                    </label>
                    <input
                      type="text"
                      name="salesRep"
                      value={formData.salesRep}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nome do vendedor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custo de Aquisição
                    </label>
                    <input
                      type="number"
                      name="acquisitionCost"
                      value={formData.acquisitionCost}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxa de Estruturação
                    </label>
                    <input
                      type="number"
                      name="structuringFee"
                      value={formData.structuringFee}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxa de Gestão
                    </label>
                    <input
                      type="number"
                      name="managementFee"
                      value={formData.managementFee}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Criar Conversão
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de detalhes da conversão */}
      {showDetailModal && selectedConversion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Detalhes da Conversão</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Informações da Carteira</h4>
                  <div className="space-y-2">
                    <p><strong>Tipo:</strong> {conversionService.getConversionTypeLabel(selectedConversion.type)}</p>
                    <p><strong>Valor:</strong> {conversionService.formatCurrency(selectedConversion.value)}</p>
                    <p><strong>IRR Esperado:</strong> {conversionService.formatPercentage(selectedConversion.expectedIrr)}</p>
                    <p><strong>Quantidade de Créditos:</strong> {selectedConversion.creditsCount || 'N/A'}</p>
                    <p><strong>Prazo Médio:</strong> {selectedConversion.averageMaturity ? `${selectedConversion.averageMaturity} meses` : 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Informações Comerciais</h4>
                  <div className="space-y-2">
                    <p><strong>Status:</strong> {conversionService.getConversionStatusLabel(selectedConversion.status)}</p>
                    <p><strong>Canal:</strong> {selectedConversion.channel}</p>
                    <p><strong>Representante:</strong> {selectedConversion.salesRep || 'N/A'}</p>
                    <p><strong>Data de Conversão:</strong> {new Date(selectedConversion.convertedAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
              </div>

              {selectedConversion.lead && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Dados do Lead</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p><strong>Nome:</strong> {selectedConversion.lead.name}</p>
                        <p><strong>Email:</strong> {selectedConversion.lead.email}</p>
                      </div>
                      <div>
                        <p><strong>Empresa:</strong> {selectedConversion.lead.company || 'N/A'}</p>
                        <p><strong>Telefone:</strong> {selectedConversion.lead.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p><strong>Tempo de Conversão:</strong> {
                          conversionService.getConversionTime(
                            selectedConversion.lead.createdAt,
                            selectedConversion.convertedAt
                          )
                        }</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(selectedConversion.structuringFee || selectedConversion.managementFee || selectedConversion.acquisitionCost) && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Custos e Taxas</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p><strong>Custo de Aquisição:</strong> {conversionService.formatCurrency(selectedConversion.acquisitionCost)}</p>
                      </div>
                      <div>
                        <p><strong>Taxa de Estruturação:</strong> {conversionService.formatCurrency(selectedConversion.structuringFee)}</p>
                      </div>
                      <div>
                        <p><strong>Taxa de Gestão:</strong> {conversionService.formatCurrency(selectedConversion.managementFee)}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p><strong>Total em Taxas:</strong> {conversionService.formatCurrency(conversionService.getTotalFees(selectedConversion))}</p>
                      {conversionService.calculateROI(selectedConversion) && (
                        <p><strong>ROI:</strong> <span className="text-green-600">{conversionService.calculateROI(selectedConversion).toFixed(1)}%</span></p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversoes;